import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import Link from 'next/link';
import { INearbyResort, INearbyResortWDistance } from '@/api/region/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { useDistanceFormatter } from '@/i18n/unitFormat';
import { RegularAd } from '@/tracking/ads/components/RegularAd';
import { fetchNearbyResorts } from '@/api/home/static';
import { SortingType } from '@/components/common/Grid/types';
import { Routes } from '@/routes';
import { OpenStatus } from '@/components/blocks/OpenStatus';
import { Diagnostics } from '@/util/diagnostics';
import { DataTable, DataTableSort } from '@/components/common/DataTable';
import { MobileDataCard } from '@/components/common/MobileDataCard';
import { SectionCard } from '@/components/ui/section-card';
import { Skeleton } from '@/components/ui/skeleton';

export interface IProps {
  resorts: INearbyResort[];
  isFresh: boolean;
  from: { latitude: number, longitude: number } | null;
}

// Sort keys are passed straight through to the API as `orderBy`; they
// match the column dataKeys the legacy Grid emitted so backend
// behaviour is preserved.
const SORT_KEY = {
  title: 'title',
  distance: 'distance',
  last24: 'last-snow',
  base: 'snow-base',
  runsOpen: 'runs-open',
};

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

function getDistanceFromLatLonInKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2))
    * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export const NearbyResortsBlock = ({ resorts, isFresh, from }: IProps) => {
  const { t } = useOTSTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');
  const distance = useDistanceFormatter('kmOrMile');
  const dash = t('common.dash');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resortsList, setResortsList] = useState<Array<INearbyResort>>(resorts);
  const [sort, setSort] = useState<DataTableSort | null>(null);

  const onSortChange = useCallback(async (next: DataTableSort) => {
    setSort(next);
    setIsLoading(true);
    try {
      const response = await fetchNearbyResorts(
        from?.latitude,
        from?.longitude,
        next.key,
        next.dir as SortingType,
      );
      setResortsList(response);
    } catch (e: any) {
      Diagnostics.error({
        tag: 'nearby-resorts',
        error: e,
        message: 'Failed to load nearby',
        data: { sort: next },
      });
    }
    setIsLoading(false);
  }, [from]);

  useEffect(() => {
    setResortsList(resorts);
  }, [resorts]);

  const rows: Array<INearbyResortWDistance> = useMemo(
    () => resortsList.map((resort) => ({
      ...resort,
      distance: from
        ? getDistanceFromLatLonInKm(resort.latitude, resort.longitude, from.latitude, from.longitude)
        : 0,
    })),
    [resortsList, from],
  );

  if (isFresh && !isLoading && resorts.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
        {t('nearbyResorts.empty')}
      </div>
    );
  }

  if (resorts.length === 0) {
    return null;
  }

  // Cell content swaps to a Skeleton box while a refetch is in flight
  // (location change). Layout/dimensions are preserved exactly; only the
  // text content gets replaced by the pulsing placeholder.
  const numberCell = (value: React.ReactNode) => (
    isLoading ? <Skeleton className="ml-auto h-4 w-12" /> : value
  );
  const statValue = (value: React.ReactNode, skeletonWidth = 'w-10') => (
    isLoading ? <Skeleton className={`h-4 ${skeletonWidth}`} /> : value
  );

  return (
    <section className="space-y-6">
      <div className="hidden md:block">
        <DataTable sort={sort} onSortChange={onSortChange}>
          <DataTable.Head>
            <DataTable.HeadRow>
              <DataTable.HeadCell sortKey={SORT_KEY.title}>
                {t('region.liftTickets.resort')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right" sortKey={SORT_KEY.distance}>
                {t('nearbyResorts.distance')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right" sortKey={SORT_KEY.last24}>
                {t('region.skiReport.snowfall-24h')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right" sortKey={SORT_KEY.base}>
                {t('region.skiReport.baseDepth')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right" sortKey={SORT_KEY.runsOpen}>
                {t('region.skiReport.openTrails')}
              </DataTable.HeadCell>
            </DataTable.HeadRow>
          </DataTable.Head>
          <DataTable.Body>
            {rows.map((r, i) => {
              const runsOpen = r.runs?.open;
              const runsTotal = r.runs?.total;
              return (
                <DataTable.Row key={r.uuid} zebra={i % 2 === 0}>
                  <DataTable.RowHeader>
                    <div className="flex flex-col gap-0.5">
                      {isLoading ? (
                        <>
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="mt-1 h-3 w-16" />
                        </>
                      ) : (
                        <>
                          <Link
                            href={Routes.resortSnowReport({ ...r, region: r.region.slug })}
                            className="hover:text-primary hover:underline"
                          >
                            {r.title}
                          </Link>
                          <OpenStatus flag={r?.status?.openFlag} />
                        </>
                      )}
                    </div>
                  </DataTable.RowHeader>
                  <DataTable.Cell align="right">
                    {numberCell(r.distance ? distance(r.distance) : dash)}
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    {numberCell(r.snow?.last24 ? dt(r.snow.last24) : dash)}
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    {numberCell(r.snow?.base ? dt(r.snow.base) : dash)}
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    {numberCell(runsTotal !== undefined ? (
                      <>
                        {runsOpen ?? 0}
                        {' / '}
                        <span className="text-muted-foreground">{runsTotal}</span>
                      </>
                    ) : dash)}
                  </DataTable.Cell>
                </DataTable.Row>
              );
            })}
          </DataTable.Body>
        </DataTable>
      </div>

      <div className="md:hidden">
        <SectionCard as="div" padding="none" className="overflow-hidden">
          {rows.map((r) => {
            const runsOpen = r.runs?.open;
            const runsTotal = r.runs?.total;
            return (
              <MobileDataCard
                key={r.uuid}
                layout="grid"
                heading={isLoading ? (
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    <Link
                      href={Routes.resortSnowReport({ ...r, region: r.region.slug })}
                      className="hover:text-primary hover:underline"
                    >
                      {r.title}
                    </Link>
                    <div className="text-xs font-normal">
                      <OpenStatus flag={r?.status?.openFlag} />
                    </div>
                  </div>
                )}
              >
                <MobileDataCard.Stat
                  label={t('nearbyResorts.distance')}
                  value={statValue(r.distance ? distance(r.distance) : dash)}
                />
                <MobileDataCard.Stat
                  label={t('region.skiReport.snowfall-24h')}
                  value={statValue(r.snow?.last24 ? dt(r.snow.last24) : dash)}
                />
                <MobileDataCard.Stat
                  label={t('region.skiReport.baseDepth')}
                  value={statValue(r.snow?.base ? dt(r.snow.base) : dash)}
                />
                <MobileDataCard.Stat
                  label={t('region.skiReport.openTrails')}
                  value={statValue(runsTotal !== undefined ? `${runsOpen ?? 0} / ${runsTotal}` : dash, 'w-12')}
                />
              </MobileDataCard>
            );
          })}
        </SectionCard>
      </div>

      <RegularAd />
    </section>
  );
};
